import React, { useState, useEffect } from "react";
import { NavLink, Outlet, useLocation } from "react-router-dom";
import {
  Box,
  Typography,
  List,
  ListItemButton,
  ListItemText,
  Chip,
  Collapse,
  IconButton,
  alpha,
} from "@mui/material";
import {
  Person as PersonIcon,
  LocalHospital as MedicineIcon,
  Psychology as MentalIcon,
  History as HistoryIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
} from "@mui/icons-material";

const SECTIONS = [
  {
    key: "client",
    title: "Client",
    icon: PersonIcon,
    color: "#2469F0",
    items: [
      { label: "Allergies", to: "client-allergies" },
      { label: "Visit Checklist", to: "visit-checklist" },
    ],
  },
  {
    key: "clinical",
    title: "Clinical",
    icon: MedicineIcon,
    color: "#F59E0B",
    items: [
      { label: "Medicine", to: "medicine" },
      { label: "Risk Types", to: "risk-types" },
    ],
  },
  {
    key: "care",
    title: "Care & Support",
    icon: MentalIcon,
    color: "#10B981",
    items: [
      { label: "Mental Capacity", to: "mental-capacity" },
      { label: "Body Map", to: "body-map" },
      { label: "Vaccination Name", to: "vaccinations" },
    ],
  },
  {
    key: "system",
    title: "System",
    icon: HistoryIcon,
    color: "#94A3B8",
    items: [
      { label: "Weekly Scheduler Logs History", to: "schedule-logs" },
    ],
  },
];

const findActiveSectionKey = (pathname) => {
  for (const section of SECTIONS) {
    for (const item of section.items) {
      if (pathname.includes(item.to)) return section.key;
    }
  }
  return SECTIONS[0].key;
};

const ReferenceDataManagement = () => {
  const location = useLocation();
  const activeSectionKey = findActiveSectionKey(location.pathname);

  const [openSections, setOpenSections] = useState({
    [activeSectionKey]: true,
  });

  useEffect(() => {
    setOpenSections((prev) => ({
      ...prev,
      [activeSectionKey]: true,
    }));
  }, [activeSectionKey]);

  const toggleSection = (key) => {
    setOpenSections((prev) => ({
      ...prev,
      [key]: !prev[key],
    }));
  };

  return (
    <Box sx={{ display: "flex", minHeight: "100vh", bgcolor: "#F8FAFC" }}>
      {/* SIDEBAR */}
      <Box
        sx={{
          width: { xs: "100%", md: "20%", lg: "18%" },
          bgcolor: "#FFFFFF",
          borderRight: "1px solid #E2E8F0",
          display: "flex",
          flexDirection: "column",
          flexShrink: 0,
          overflowY: "auto",
        }}
      >
        {/* HEADER */}
        <Box
          sx={{
            px: 2.5,
            py: 2,
            borderBottom: "1px solid #E2E8F0",
            bgcolor: "#F8FAFC",
          }}
        >
          <Typography
            variant="subtitle1"
            fontWeight={700}
            color="#1A1A2E"
            sx={{ fontSize: "0.95rem" }}
          >
            Reference Data
          </Typography>
          <Typography
            variant="caption"
            color="#64748b"
            sx={{ fontSize: "0.7rem" }}
          >
            Manage system lookups
          </Typography>
        </Box>

        {/* CONTENT */}
        <Box sx={{ p: 2, flex: 1 }}>
          {SECTIONS.map((section) => {
            const Icon = section.icon;
            const isOpen = !!openSections[section.key];
            const hasActiveChild = activeSectionKey === section.key;

            return (
              <Box key={section.key} sx={{ mb: 1.5 }}>
                {/* SECTION HEADER */}
                <Box
                  onClick={() => toggleSection(section.key)}
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 1,
                    px: 1,
                    py: 0.75,
                    borderRadius: "6px",
                    cursor: "pointer",
                    bgcolor: hasActiveChild
                      ? alpha(section.color, 0.06)
                      : "transparent",
                    "&:hover": {
                      bgcolor: hasActiveChild
                        ? alpha(section.color, 0.1)
                        : "#F8FAFC",
                    },
                    transition: "background-color 0.15s ease",
                  }}
                >
                  <Box
                    sx={{
                      width: 24,
                      height: 24,
                      borderRadius: "6px",
                      bgcolor: alpha(section.color, 0.1),
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      flexShrink: 0,
                    }}
                  >
                    <Icon sx={{ fontSize: 14, color: section.color }} />
                  </Box>

                  <Typography
                    variant="caption"
                    fontWeight={700}
                    color={hasActiveChild ? section.color : "#475467"}
                    sx={{
                      textTransform: "uppercase",
                      letterSpacing: "0.5px",
                      fontSize: "0.68rem",
                      flex: 1,
                    }}
                  >
                    {section.title}
                  </Typography>

                  <IconButton
                    size="small"
                    sx={{
                      p: 0.25,
                      color: hasActiveChild ? section.color : "#94A3B8",
                    }}
                  >
                    {isOpen ? (
                      <ExpandLessIcon sx={{ fontSize: 16 }} />
                    ) : (
                      <ExpandMoreIcon sx={{ fontSize: 16 }} />
                    )}
                  </IconButton>
                </Box>

                {/* COLLAPSIBLE ITEMS */}
                <Collapse in={isOpen} timeout="auto" unmountOnExit>
                  <List component="nav" sx={{ p: 0, mt: 0.5 }}>
                    {section.items.map((item) => {
                      const isActive = location.pathname.includes(item.to);
                      return (
                        <ListItemButton
                          key={item.to}
                          component={NavLink}
                          to={item.to}
                          sx={{
                            borderRadius: "6px",
                            mb: 0.25,
                            py: 0.75,
                            px: 1.5,
                            ml: 1,
                            minHeight: 34,
                            bgcolor: isActive
                              ? alpha(section.color, 0.08)
                              : "transparent",
                            "&:hover": {
                              bgcolor: isActive
                                ? alpha(section.color, 0.12)
                                : "#F8FAFC",
                            },
                            "&.active": {
                              bgcolor: alpha(section.color, 0.08),
                            },
                          }}
                        >
                          <ListItemText
                            primary={item.label}
                            primaryTypographyProps={{
                              fontSize: "0.78rem",
                              fontWeight: isActive ? 600 : 500,
                              color: isActive ? section.color : "#475467",
                            }}
                          />
                          {isActive && (
                            <Box
                              sx={{
                                width: 6,
                                height: 6,
                                borderRadius: "50%",
                                bgcolor: section.color,
                              }}
                            />
                          )}
                        </ListItemButton>
                      );
                    })}
                  </List>
                </Collapse>
              </Box>
            );
          })}
        </Box>

        {/* FOOTER */}
        <Box
          sx={{
            px: 2,
            py: 1.5,
            borderTop: "1px solid #E2E8F0",
            bgcolor: "#F8FAFC",
          }}
        >
          <Chip
            label={`${SECTIONS.reduce((acc, s) => acc + s.items.length, 0)} items`}
            size="small"
            sx={{
              height: 20,
              fontSize: "0.65rem",
              fontWeight: 600,
              bgcolor: alpha("#2469F0", 0.08),
              color: "#2469F0",
            }}
          />
        </Box>
      </Box>

      {/* MAIN CONTENT */}
      <Box
        sx={{
          flex: 1,
          width: { xs: "100%", md: "80%", lg: "82%" },
          p: { xs: 2, md: 3, lg: 4 },
          overflowY: "auto",
        }}
      >
        <Outlet />
      </Box>
    </Box>
  );
};

export default ReferenceDataManagement;